import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type FormEvent } from 'react';
import { api, ApiError, fieldIssues } from '../api';
import { leaveScreen, useUnsavedGuard } from '../ui/unsaved';
import { can } from '../auth/me';
import { useMe } from '../auth/useMe';
import { ConflictNotice } from '../ui/ConflictNotice';
import { BusyList, formatDayRange, kindLabel, type Occupancy } from './BusyList';
import { BlocksSection } from './Blocks';
import { NumberInput } from '../ui/NumberInput';
import { NotesInput } from '../ui/NotesInput';
import { useUrlState } from '../nav/urlState';
import { PeoplePanel } from './PeoplePanel';
import { PEOPLE_AVAILABILITY_RESOURCE } from './peopleGrid';
import { Notice } from '../ui/Notice';

const KEY = ['availability'] as const;
const RESOURCE = 'availability.reservations';

type Availability = { itemId: string; stock: number; free: number; hardPeak: number; softPeak: number; busyIn: Occupancy[] };
type Manual = { id: string; itemId: string; quantity: number; kind: 'soft' | 'hard'; hardFailed: boolean; note: string; firstDay?: string; lastDay?: string; version: number };
type Created = { id: string; kind: 'soft' | 'hard'; hardFailed: boolean; missing: number };
type Picked = { id: string; name: string };

const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw' }).format(new Date());

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

/** Catalog item search: type, then pick one from the results. */
function ItemPicker({ picked, onPick }: { picked: Picked | null; onPick: (item: Picked) => void }) {
  const [text, setText] = useState('');
  const q = useDebounced(text.trim(), 250);
  const results = useQuery({
    queryKey: [...KEY, 'picker', q],
    enabled: q !== '',
    queryFn: () => api<{ items: Picked[] }>(`/api/catalog/items?q=${encodeURIComponent(q)}`),
  });
  return (
    <div>
      <label className="field">
        <span>Pozycja katalogu</span>
        <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder="Szukaj pozycji po nazwie" autoComplete="off" />
      </label>
      {picked && <p>Wybrana pozycja: <strong>{picked.name}</strong></p>}
      {text.trim() !== '' && q !== '' && results.data && results.data.items.length === 0 && <p className="muted">Nic nie znaleziono.</p>}
      {text.trim() !== '' && results.data && results.data.items.length > 0 && (
        <ul className="list" aria-label="Wyniki wyszukiwania">
          {results.data.items.slice(0, 10).map((i) => (
            <li key={i.id} className="row">
              <div className="row-main">
                <strong>{i.name}</strong>
              </div>
              <button
                className="btn small"
                type="button"
                aria-label={`Wybierz: ${i.name}`}
                onClick={() => {
                  onPick({ id: i.id, name: i.name });
                  setText('');
                }}
              >
                Wybierz
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function ManualList({ names }: { names: Map<string, string> }) {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: [...KEY, 'manual'], queryFn: () => api<{ reservations: Manual[] }>('/api/availability/manual') });
  const remove = useMutation({
    mutationFn: (r: Manual) => api(`/api/availability/manual/${r.id}?version=${r.version}`, { method: 'DELETE' }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: KEY }),
  });
  const rows = list.data?.reservations ?? [];
  // Names of items we have not picked on this screen: asked from the catalog one by one (a role without catalog access just sees no name).
  const missing = [...new Set(rows.map((r) => r.itemId))].filter((id) => !names.has(id));
  const fetched = useQueries({
    queries: missing.map((id) => ({ queryKey: [...KEY, 'item', id], retry: false, queryFn: () => api<{ name: string }>(`/api/catalog/items/${id}`) })),
  });
  const nameOf = (id: string) => names.get(id) ?? fetched[missing.indexOf(id)]?.data?.name ?? 'Pozycja katalogu';
  const error = remove.error instanceof ApiError ? remove.error : null;
  if (!me) return null;
  return (
    <section className="panel" aria-label="Rezerwacje ręczne">
      <h3>Rezerwacje ręczne</h3>
      {error?.status === 409 && <ConflictNotice onReload={() => remove.reset()} />}
      {error && error.status !== 409 && (
        <p className="alert" role="alert">
          {error.message}
        </p>
      )}
      {rows.length === 0 ? (
        <p className="muted">Brak rezerwacji ręcznych.</p>
      ) : (
        <ul className="list">
          {rows.map((r) => (
            <li key={r.id} className="row">
              <div className="row-main">
                <strong>{nameOf(r.itemId)}</strong> <span className="chip">{kindLabel(r.kind)}</span>
                {r.hardFailed && <span className="chip sensitive">brakowało sprzętu</span>}
                <div className="muted">
                  Ilość: {r.quantity} · {formatDayRange(r.firstDay, r.lastDay)}
                  {r.note && ` · ${r.note}`}
                </div>
              </div>
              {can(me, RESOURCE, 'delete') && (
                <button className="btn small" type="button" aria-label={`Usuń rezerwację: ${r.note || nameOf(r.itemId)}`} disabled={remove.isPending} onClick={() => remove.mutate(r)}>
                  Usuń
                </button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/**
 * „Dostępność” with two tabs (spec 2026-10-25): „Ludzie” (the people × days grid) first and opened by default (owner
 * 2026-10-25 21:54), then „Sprzęt” (`?widok=sprzet`, the gear). Each tab for whoever may see it; a user with only one of
 * them gets it without the tabs. The old link `?widok=ludzie` still opens „Ludzie”.
 */
export function AvailabilityPage() {
  const { me } = useMe();
  const { params, update } = useUrlState();
  if (!me) return null;
  const gear = can(me, RESOURCE, 'view');
  const people = can(me, PEOPLE_AVAILABILITY_RESOURCE, 'view');
  const showPeople = people && (!gear || params.get('widok') !== 'sprzet');
  return (
    <section className={`page${showPeople ? ' wide' : ''}`} data-feature-id={showPeople ? 'crew.people_availability' : 'availability.check'}>
      <div className="page-head">
        <h1>Dostępność</h1>
      </div>
      {gear && people && (
        <nav className="segment" aria-label="Dostępność">
          <button type="button" aria-pressed={showPeople} onClick={() => update({ widok: null })}>
            Ludzie
          </button>
          {/* owner 2026-10-27 12:41: leaving „Ludzie” with questions not sent asks first (`Unsent`) */}
          <button type="button" aria-pressed={!showPeople} onClick={() => leaveScreen('/dostepnosc?widok=sprzet', () => update({ widok: 'sprzet', miesiac: null, funkcja: null, osoby: null }))}>
            Sprzęt
          </button>
        </nav>
      )}
      {showPeople ? <PeoplePanel /> : gear ? <GearAvailability /> : null}
    </section>
  );
}

/** „Dostępność → Sprzęt”: how many pieces of an item are free in the chosen days, and reserving by hand. */
function GearAvailability() {
  const { me } = useMe();
  const queryClient = useQueryClient();
  const [item, setItem] = useState<Picked | null>(null);
  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [quantity, setQuantity] = useState('1');
  const [kind, setKind] = useState<'soft' | 'hard'>('soft');
  const [note, setNote] = useState('');
  const [shortage, setShortage] = useState(0);
  const [names, setNames] = useState(() => new Map<string, string>());

  const validRange = /^\d{4}-\d{2}-\d{2}$/.test(from) && /^\d{4}-\d{2}-\d{2}$/.test(to) && from <= to;
  const result = useQuery({
    queryKey: [...KEY, 'check', item?.id, from, to],
    enabled: item !== null && validRange,
    queryFn: () => api<Availability>(`/api/availability?itemId=${item!.id}&from=${from}&to=${to}`),
  });
  const reserve = useMutation({
    mutationFn: () => api<Created>('/api/availability/manual', { method: 'POST', body: { itemId: item!.id, quantity: Number(quantity), firstDay: from, lastDay: to, kind, note } }),
    onSuccess: async (created) => {
      setShortage(created.hardFailed ? created.missing : 0);
      setNote('');
      await queryClient.invalidateQueries({ queryKey: KEY });
    },
  });
  const validQuantity = /^\d+$/.test(quantity.trim()) && Number(quantity) > 0;
  // owner 2026-10-23 21:14: a note typed and not reserved yet: leaving asks first
  const guardRef = useUnsavedGuard({
    dirty: item !== null && note.trim() !== '',
    save: async () => {
      if (!item || !validQuantity || !validRange) return false;
      await reserve.mutateAsync();
      return true;
    },
  });
  if (!me) return null;
  const error = reserve.error instanceof ApiError ? reserve.error : null;

  function submit(event: FormEvent) {
    event.preventDefault();
    setShortage(0);
    if (item && validQuantity && validRange) reserve.mutate();
  }

  return (
    <>
      <div className="panel form">
        <ItemPicker
          picked={item}
          onPick={(i) => {
            setItem(i);
            setNames(new Map(names).set(i.id, i.name));
            setShortage(0);
          }}
        />
        <div className="filters">
          <label className="field">
            <span>Od dnia</span>
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} />
          </label>
          <label className="field">
            <span>Do dnia</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} />
          </label>
        </div>
        {!validRange && <p className="alert" role="alert">Ostatni dzień nie może być przed pierwszym.</p>}
      </div>

      {item && result.data && (
        <>
          <p className="panel" role="status" aria-label="Wynik">
            <strong>Wolne: {result.data.free} z {result.data.stock}</strong>
          </p>
          <BusyList busyIn={result.data.busyIn} from={from} to={to} />
        </>
      )}
      {item && <BlocksSection itemId={item.id} itemName={item.name} />}
      {result.error && (
        <p className="alert" role="alert">
          {result.error instanceof ApiError ? result.error.message : 'Nie udało się sprawdzić dostępności'}
        </p>
      )}

      {item && can(me, RESOURCE, 'create') && (
        <form ref={guardRef} className="panel form" aria-label="Rezerwacja ręczna" data-feature-id="availability.manual" onSubmit={submit}>
          <h3>Zarezerwuj ręcznie: {item.name}</h3>
          {shortage > 0 && (
            <p className="alert" role="alert">
              Brakuje: {shortage}. Rezerwacja zapisana jako wstępna.
            </p>
          )}
          {error && (
            <p className="alert" role="alert">
              {error.message}
              {fieldIssues(error).map((i) => (
                <span key={i.path + i.message}> {i.message}</span>
              ))}
            </p>
          )}
          {reserve.isSuccess && shortage === 0 && !error && (
            <Notice>
              Zapisano rezerwację.
            </Notice>
          )}
          <label className="field">
            <span>Ilość</span>
            <NumberInput kind="integer" value={quantity} onValueChange={setQuantity} />
          </label>
          <label className="field">
            <span>Rodzaj</span>
            <select value={kind} onChange={(e) => setKind(e.target.value as 'soft' | 'hard')}>
              <option value="soft">wstępna (nie blokuje, ale zajmuje sztuki)</option>
              <option value="hard">pewna (zajmuje sprzęt)</option>
            </select>
          </label>
          <label className="field">
            <span>Notatka</span>
            <NotesInput value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} />
          </label>
          <div className="actions">
            <button className="btn primary" type="submit" disabled={reserve.isPending || !validQuantity || !validRange}>
              {reserve.isPending ? 'Zapisywanie…' : 'Zarezerwuj ręcznie'}
            </button>
          </div>
        </form>
      )}

      <ManualList names={names} />
    </>
  );
}
